import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { trpc } from '@/trpc/client';
import { unifiedDiff } from './diff';

export function HistoryModal(): JSX.Element {
  const { key = '' } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const { data: current } = trpc.templates.get.useQuery({ key } as Parameters<
    typeof trpc.templates.get.useQuery
  >[0]);
  const { data, isLoading } = trpc.templates.history.useQuery({ key } as Parameters<
    typeof trpc.templates.history.useQuery
  >[0]);
  const [target, setTarget] = useState<{ version: string; text: string } | null>(null);
  const rollback = trpc.templates.rollback.useMutation({
    onSuccess: async () => {
      await utils.templates.list.invalidate();
      await utils.templates.get.invalidate({ key } as Parameters<
        typeof utils.templates.get.invalidate
      >[0]);
      setTarget(null);
      navigate(`/templates/${key}`);
    },
  });
  const canRollback = user?.role === 'editor' || user?.role === 'admin';

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/30">
      <div className="w-[720px] rounded bg-white p-4 shadow">
        <header className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">{key} の履歴</h2>
          <button
            type="button"
            className="rounded border px-2 py-1 text-sm"
            onClick={() => navigate(`/templates/${key}`)}
          >
            閉じる
          </button>
        </header>
        {isLoading ? (
          <p>読み込み中...</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-600">
              <tr>
                <th className="px-2 py-1 text-left">version</th>
                <th className="px-2 py-1 text-left">更新日時</th>
                <th className="px-2 py-1 text-left">更新者</th>
                <th className="px-2 py-1 text-left">先頭 40 文字</th>
                <th className="px-2 py-1 text-left" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data?.items ?? []).map((row) => (
                <tr key={row.version}>
                  <td className="px-2 py-1 font-mono">{row.version}</td>
                  <td className="px-2 py-1 text-xs text-gray-500">{row.updatedAt}</td>
                  <td className="px-2 py-1 text-xs">{row.updatedBy?.email ?? '—'}</td>
                  <td className="px-2 py-1 max-w-xs truncate">{row.text.slice(0, 40)}</td>
                  <td className="px-2 py-1">
                    {canRollback && (
                      <button
                        type="button"
                        className="rounded border px-2 py-0.5 text-xs hover:bg-gray-50"
                        onClick={() => setTarget({ version: row.version, text: row.text })}
                      >
                        このバージョンに戻す
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {target && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/40">
            <div className="w-[600px] rounded bg-white p-4 shadow">
              <h3 className="mb-2 text-base font-semibold">{target.version} に戻す</h3>
              <div className="mb-3 max-h-64 overflow-auto rounded border bg-gray-50 p-2 font-mono text-xs">
                {unifiedDiff(current?.text ?? '', target.text).map((line, idx) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: diff lines have no stable id
                    key={idx}
                    className={
                      line.kind === 'add'
                        ? 'bg-green-100'
                        : line.kind === 'remove'
                          ? 'bg-red-100'
                          : ''
                    }
                  >
                    {line.kind === 'add' ? '+ ' : line.kind === 'remove' ? '- ' : '  '}
                    {line.text}
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded border px-3 py-1 text-sm"
                  onClick={() => setTarget(null)}
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  className="rounded bg-blue-600 px-3 py-1 text-sm text-white"
                  disabled={rollback.isPending}
                  onClick={() =>
                    rollback.mutate({ key, version: target.version } as Parameters<
                      typeof rollback.mutate
                    >[0])
                  }
                >
                  ロールバックして反映
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
